/* iter55g48 — Persistent "Exports in progress" indicator.
 *
 * Sits in the MenuBar right-chrome group. Polls
 * `GET /api/exports/pdf-jobs?limit=10` every 8 seconds while a job
 * is queued or running; falls back to 30 s once everything has
 * settled. Shows an animated pill with the live count so writers
 * can leave the ExportDialog and still know when their Adobe PDF
 * is ready to download. Clicking opens the ExportDialog directly to
 * the Jobs panel.
 *
 * The endpoint is auth-scoped so signed-out visitors on the landing
 * never poll. Returns `[]` when the user has no jobs yet — quiet UI.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Download, Loader2, CheckCircle2 } from 'lucide-react';
import { exportApi } from '@/lib/api';
import { usePW } from '@/lib/store';

const ACTIVE_POLL_MS   = 8000;
const IDLE_POLL_MS     = 30000;

export default function ExportsIndicator({ onOpen }) {
  const { user } = usePW();
  const [jobs, setJobs]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const timerRef                = useRef(null);

  useEffect(() => {
    // Only poll for signed-in users.
    if (!user?.user_id) return undefined;
    let cancelled = false;

    const tick = async () => {
      if (cancelled) return;
      setLoading(true);
      try {
        const r = await exportApi.listPdfJobs(10);
        if (cancelled) return;
        setJobs(Array.isArray(r?.jobs) ? r.jobs : (Array.isArray(r) ? r : []));
      } catch { /* silent — indicator is a non-critical widget */ }
      finally { if (!cancelled) setLoading(false); }
      const anyActive = (Array.isArray(jobs) ? jobs : [])
        .some((j) => j.status === 'queued' || j.status === 'running');
      const delay = anyActive ? ACTIVE_POLL_MS : IDLE_POLL_MS;
      timerRef.current = setTimeout(tick, delay);
    };
    tick();
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  const active   = jobs.filter((j) => j.status === 'queued' || j.status === 'running');
  const complete = jobs.filter((j) => j.status === 'complete' && !j.acknowledged);

  if (active.length === 0 && complete.length === 0) return null;

  const label = active.length > 0
    ? `${active.length} export${active.length === 1 ? '' : 's'} running`
    : `${complete.length} export${complete.length === 1 ? '' : 's'} ready`;
  const tone = active.length > 0
    ? 'bg-amber-500/15 text-amber-800 hover:bg-amber-500/25 border-amber-500/40'
    : 'bg-emerald-500/15 text-emerald-800 hover:bg-emerald-500/25 border-emerald-500/40';

  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid="menubar-exports-indicator"
      title={label}
      className={
        'inline-flex items-center gap-1 rounded-sm border px-1.5 py-0.5 text-[10.5px] font-semibold transition '
        + tone
      }
    >
      {active.length > 0
        ? <Loader2 className="h-3 w-3 animate-spin" />
        : <CheckCircle2 className="h-3 w-3" />}
      <Download className="h-2.5 w-2.5 opacity-70" />
      <span className="tabular-nums">{label}</span>
      {loading && <span className="ml-0.5 h-1 w-1 animate-pulse rounded-full bg-current opacity-50" />}
    </button>
  );
}
